// Domain-aware editions. Same codebase/backend/DB.
import axios from "axios";

// - alumnipulse.net  -> global edition (all countries, India included)
// - indiaalumni.com  -> India edition (India-first content, own branding)
// Preview override: append ?edition=india or ?edition=global (persists in localStorage).

const INDIA_HOSTS = ["indiaalumni.com", "www.indiaalumni.com", "indiaalumni.net", "www.indiaalumni.net"];

function computeEditionId() {
  // Deployment-level override — used by a dedicated IndiaAlumni deployment/fork
  // (set REACT_APP_EDITION=india in that deployment's frontend env).
  const forced = (process.env.REACT_APP_EDITION || "").toLowerCase();
  if (forced === "india") return "india";
  if (forced === "global") return "global";

  // Path-based entry: /india (used with registrar forwarding indiaalumni.com -> /india).
  try {
    const path = (window.location.pathname || "").toLowerCase();
    if (path === "/india" || path.startsWith("/india/")) {
      localStorage.setItem("ap_edition", "india");
      return "india";
    }
  } catch (_) {}

  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("edition");
    if (q === "india" || q === "global") localStorage.setItem("ap_edition", q);
  } catch (_) {}

  const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  if (INDIA_HOSTS.some((h) => host === h || host.endsWith("." + h))) return "india";

  let stored = null;
  try { stored = localStorage.getItem("ap_edition"); } catch (_) {}
  if (stored === "india" || stored === "global") return stored;

  return "global";
}

const GLOBAL = {
  id: "global",
  brand: "AlumniPulse",
  mark: "™",
  domain: "alumnipulse.net",
  url: "https://alumnipulse.net",
  logo: "/alumnipulse-logo.png",
  country: null, // no scoping
  scope: {},     // {country?, state?, region?} applied to browse + partner reads
  copyright: "© 2026 AlumniPulse.net — Social Pulse Inc",
  other: { brand: "IndiaAlumni", url: "https://indiaalumni.com", label: "IndiaAlumni.com", note: "Indian alumni? Visit" },
  emails: {
    noreply: "noreply@alumnipulse.net",
    contact: "contact@alumnipulse.net",
    info: "info@alumnipulse.net",
    privacy: "privacy@alumnipulse.net",
    abuse: "abuse@alumnipulse.net",
  },
};

const INDIA = {
  id: "india",
  brand: "IndiaAlumni",
  mark: "™",
  domain: "indiaalumni.com",
  url: "https://indiaalumni.com",
  logo: "/indiaalumni-logo.png",
  country: "India", // scope public browsing to India
  scope: { country: "India" }, // {country?, state?, region?}
  copyright: "© 2026 IndiaAlumni.com — Social Pulse Inc",
  other: { brand: "AlumniPulse", url: "https://alumnipulse.net", label: "AlumniPulse.net", note: "Looking for alumni outside India? Visit" },
  emails: {
    noreply: "noreply@indiaalumni.com",
    support: "support@indiaalumni.com",
    contact: "contact@indiaalumni.com",
    info: "info@indiaalumni.com",
    privacy: "legal@indiaalumni.com",
    legal: "legal@indiaalumni.com",
    admin: "admin@indiaalumni.com",
    abuse: "abuse@indiaalumni.com",
  },
};

// ---------------------------------------------------------------------------
// Adding a new region site later (e.g. kerala.com, indias.com) is PURE CONFIG:
//   1. Add one entry to EDITIONS below with its own brand + `scope`, e.g.
//        const KERALA = { id: "kerala", brand: "KeralaAlumni", domain: "kerala.com",
//          scope: { state: "Kerala" }, hosts: ["kerala.com", "www.kerala.com"], ... };
//      then register it in EDITIONS: { global, india, kerala }.
//   2. Deploy a sister app for that domain with env:
//        REACT_APP_EDITION=kerala   (frontend, baked at build)
//        APP_EDITION=kerala + EDITION_STATE=Kerala   (backend scope — authoritative)
// No other code changes: scope flows to browse APIs, the partner API, and links.
// ---------------------------------------------------------------------------

export const EDITION_ID = computeEditionId();
export const EDITIONS = { global: GLOBAL, india: INDIA };
export const EDITION = EDITIONS[EDITION_ID] || GLOBAL;
export const isIndia = EDITION_ID === "india";
export const isRegion = EDITION_ID !== "global";

// Append this edition's scope (country/state/region) to a query string.
export function scopedPath(path, extraParams = {}) {
  const params = new URLSearchParams(extraParams);
  const scope = EDITION.scope || (EDITION.country ? { country: EDITION.country } : {});
  Object.entries(scope).forEach(([k, v]) => { if (v) params.set(k, v); });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function useEdition() {
  return EDITION;
}

// Set document title + favicon for the active edition (preview + live).
try {
  if (typeof document !== "undefined" && isIndia) {
    document.title = "IndiaAlumni — Reconnect with your Indian alma mater";
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.href = INDIA.logo;
  }
  if (isRegion) {
    axios.defaults.headers.common["X-Edition"] = EDITION_ID;
    const sc = EDITION.scope || {};
    if (sc.country) axios.defaults.headers.common["X-Edition-Country"] = sc.country;
    if (sc.state) axios.defaults.headers.common["X-Edition-State"] = sc.state;
    if (sc.region) axios.defaults.headers.common["X-Edition-Region"] = sc.region;
  }
} catch (_) {}
